import { Controller } from "@hotwired/stimulus"

// Room kept below the menu for the sticky footer bar (ERP::FooterBar, 64 px high)
const FOOTER_RESERVE = 72
const GUTTER = 8

// Only one "⋯" menu is open at a time
let openMenu = null

// "⋯" action menu (ERP::Menu::Component). The menu is a manual popover, so it lives in the top layer and a
// scrolling table cannot clip it; it opens below its trigger, or above when the footer leaves no room.
// Keyboard: arrows on the trigger open on the first or last item; in the menu, arrows, Home and End move,
// Escape closes and gives focus back to the trigger, Tab closes.
// Connects to data-controller="erp--menu"
export default class extends Controller {
  static targets = ["trigger", "menu"]

  connect() {
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this._onDocumentClick = this.#documentClick.bind(this)
    this._onViewportChange = this.#viewportChange.bind(this)
  }

  disconnect() {
    this.close({ restoreFocus: false })
  }

  toggle() {
    this.#isOpen() ? this.close() : this.open()
  }

  triggerKeydown(event) {
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return

    event.preventDefault()
    if (!this.#isOpen()) this.open({ focusLast: event.key === "ArrowUp" })
  }

  menuKeydown(event) {
    if (event.key === "Tab") return this.close({ restoreFocus: false })
    if (event.key === "Escape") {
      event.preventDefault()
      return this.close()
    }

    const items = this.#items()
    const target = this.#targetIndex(event.key, items.indexOf(document.activeElement), items.length)
    if (target === undefined) return

    event.preventDefault()
    items[(target + items.length) % items.length]?.focus()
  }

  // An item was chosen: the menu closes and the trigger takes the focus back before the action runs, so a
  // confirmation dialog opened by the action returns the focus to the trigger when it closes.
  itemClick(event) {
    if (event.target.closest("[role='menuitem']")) this.close()
  }

  open({ focusLast = false } = {}) {
    if (openMenu && openMenu !== this) openMenu.close({ restoreFocus: false })
    openMenu = this

    this.menuTarget.showPopover()
    this.triggerTarget.setAttribute("aria-expanded", "true")
    this.#position()

    const items = this.#items()
    ;(focusLast ? items[items.length - 1] : items[0])?.focus({ preventScroll: true })

    document.addEventListener("click", this._onDocumentClick, true)
    window.addEventListener("scroll", this._onViewportChange, { capture: true, passive: true })
    window.addEventListener("resize", this._onViewportChange)
  }

  close({ restoreFocus = true } = {}) {
    if (!this.#isOpen()) return

    this.menuTarget.hidePopover()
    this.triggerTarget.setAttribute("aria-expanded", "false")
    if (restoreFocus) this.triggerTarget.focus()
    if (openMenu === this) openMenu = null

    document.removeEventListener("click", this._onDocumentClick, true)
    window.removeEventListener("scroll", this._onViewportChange, { capture: true })
    window.removeEventListener("resize", this._onViewportChange)
    cancelAnimationFrame(this._frame)
  }

  // Arrows wrap around (the caller takes the index modulo the item count)
  #targetIndex(key, index, count) {
    return { ArrowDown: index + 1, ArrowUp: index - 1, Home: 0, End: count - 1 }[key]
  }

  #isOpen() {
    return this.menuTarget.matches(":popover-open")
  }

  #items() {
    return [...this.menuTarget.querySelectorAll("[role='menuitem']:not([disabled])")]
  }

  // Below the trigger, right-aligned with it; above it when the room left before the footer is too small.
  #position() {
    const trigger = this.triggerTarget.getBoundingClientRect()
    const menu = this.menuTarget.getBoundingClientRect()
    const bottomLimit = window.innerHeight - FOOTER_RESERVE
    const up = bottomLimit - trigger.bottom < menu.height + GUTTER && trigger.top > menu.height + GUTTER

    const top = up ? trigger.top - menu.height - 6 : trigger.bottom + 6
    const left = Math.max(GUTTER, Math.min(trigger.right - menu.width, window.innerWidth - menu.width - GUTTER))
    this.menuTarget.style.top = `${Math.round(top)}px`
    this.menuTarget.style.left = `${Math.round(left)}px`
    this.menuTarget.toggleAttribute("data-up", up)
  }

  #documentClick(event) {
    if (!this.element.contains(event.target)) this.close({ restoreFocus: false })
  }

  // The menu follows its trigger while the page scrolls, and closes once the trigger leaves the screen.
  #viewportChange() {
    cancelAnimationFrame(this._frame)
    this._frame = requestAnimationFrame(() => {
      const trigger = this.triggerTarget.getBoundingClientRect()
      if (trigger.bottom < 0 || trigger.top > window.innerHeight) {
        this.close({ restoreFocus: false })
      } else {
        this.#position()
      }
    })
  }
}
